import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  createMatterLostWage,
  deleteMatterLostWage,
  updateMatterLostWage,
} from "@/lib/data/requests/matters/matter-lost-wage";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  type APIError,
  type APIResponse,
  type Matter,
  type MatterLostWage,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import toast from "react-hot-toast";

const getMatterLostWages = (matter: Matter) => matter.lostWages;

const withLostWages = (
  matter: Matter,
  lostWages: MatterLostWage[],
): Matter => ({ ...matter, lostWages });

export const useCreateMatterLostWage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterLostWage>[0]) =>
      createMatterLostWage(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          const lostWages = getMatterLostWages(oldData.data);
          const nextLostWages = [
            ...lostWages,
            {
              ...data,
              dateCreated: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss"),
              dateModified: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss"),
              id: "optimistic",
            } as MatterLostWage,
          ];

          return {
            ...oldData,
            data: withLostWages(oldData.data, nextLostWages),
          };
        },
      );

      return { previousData };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error creating lost wage");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("Lost wage created successfully");
    },
  });
};

export const useUpdateMatterLostWage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterLostWage>[0]) =>
      updateMatterLostWage(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          const lostWages = getMatterLostWages(oldData.data);
          const nextLostWages = lostWages.map((lostWage) =>
            lostWage.id === data.id ? { ...lostWage, ...data } : lostWage,
          );

          return {
            ...oldData,
            data: withLostWages(oldData.data, nextLostWages),
          };
        },
      );
      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating lost wage");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("Lost wage updated successfully");
    },
  });
};

export const useDeleteMatterLostWage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterLostWage>[0]) =>
      deleteMatterLostWage(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          const lostWages = getMatterLostWages(oldData.data);
          const nextLostWages = lostWages.filter(
            (lostWage) => lostWage.id !== data.id,
          );

          return {
            ...oldData,
            data: withLostWages(oldData.data, nextLostWages),
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting lost wage");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("Lost wage deleted successfully");
    },
  });
};
